import { UTCDateMini } from '@date-fns/utc';
import { captureException } from '@sentry/react';
import {
  addDays,
  differenceInDays,
  endOfDay,
  formatDuration as fnsFormatDuration,
  isSameYear as fnsIsSameYear,
  intervalToDuration,
  isValid,
  parse,
  startOfDay,
  startOfISOWeek,
} from 'date-fns';
import { enUS } from 'date-fns/locale';
import { format } from 'date-fns-tz';

import { Timezone } from 'shared/types/dateTime';

export const DATE_FORMAT = 'MMM d, yyyy';
export const SHORT_DATE_FORMAT = 'MM/dd/yyyy';
export const FILTER_DATE_FORMAT = 'MM/dd/yy';

export const API_DATE_FORMAT = 'yyyy-MM-dd';

export const TIME_FORMAT = 'h:mm:ss a';

type DateType = Date | number | string | null;

export const formatDate = (date?: DateType, utc = false, dateFormat = DATE_FORMAT) => {
  if (!date) {
    return '–';
  }

  const fnsDate = utc ? new UTCDateMini(date) : new Date(date);

  if (!isValid(fnsDate)) {
    return '–';
  }

  try {
    return format(fnsDate, dateFormat);
  } catch (error) {
    console.error(error);
    captureException(error);

    return '–';
  }
};

export const formatDateTime = (date?: DateType, dateFormat = DATE_FORMAT, timeFormat = TIME_FORMAT) => {
  if (!date) {
    return '–';
  }

  const fnsDate = new Date(date);

  if (!isValid(fnsDate)) {
    return '–';
  }

  try {
    return format(fnsDate, `${dateFormat}, ${timeFormat} z`, { locale: enUS });
  } catch (error) {
    console.error(error);
    captureException(error);

    return '–';
  }
};

export const formatDateTimeShort = (date?: DateType, dateFormat = DATE_FORMAT) => {
  if (!date) {
    return '–';
  }

  const fnsDate = new Date(date);

  if (!isValid(fnsDate)) {
    return '–';
  }

  try {
    return format(fnsDate, `${dateFormat}, h:mm a`);
  } catch (error) {
    console.error(error);
    captureException(error);

    return '–';
  }
};

function getNow(timezone: Timezone) {
  if (timezone === Timezone.Local) {
    return new Date();
  }

  return new UTCDateMini(new Date());
}

export function getPeriodStart(days: number, timezone: Timezone) {
  return format(addDays(startOfDay(getNow(timezone)), days), API_DATE_FORMAT);
}

export function getPeriodEnd(days: number, timezone: Timezone) {
  return format(addDays(endOfDay(getNow(timezone)), days), API_DATE_FORMAT);
}

export function getWeekStart(timezone: Timezone) {
  return format(startOfISOWeek(getNow(timezone)), API_DATE_FORMAT);
}

export function isSameYear(date1?: DateType, date2?: DateType) {
  return !!date1 && !!date2 && fnsIsSameYear(date1, date2);
}

export const formatDuration = (ms: number) => {
  const duration = intervalToDuration({
    start: 0,
    end: ms,
  });

  const { hours, minutes, seconds } = duration;

  if (!hours && !minutes && !seconds) {
    return 'now';
  }

  return `in ${fnsFormatDuration(duration)}`;
};

/**
 * Convert API_DATE_FORMAT to ISO string representing the start of the day in the user's timezone
 * @param date - date in API_DATE_FORMAT
 * @param daysToAdd - add days to the date
 * @param timezone - timezone to use
 */
export const convertApiDateToISOString = (date: string, daysToAdd = 0, timezone: Timezone = Timezone.Local) => {
  const referenceDate = timezone === Timezone.UTC ? new UTCDateMini() : new Date();

  return addDays(parse(date, API_DATE_FORMAT, referenceDate), daysToAdd).toISOString();
};

export const apiDateDiff = (dateFrom: string, dateTo: string) => {
  const referenceDate = new Date();

  return differenceInDays(
    parse(dateTo, API_DATE_FORMAT, referenceDate),
    parse(dateFrom, API_DATE_FORMAT, referenceDate),
  );
};
